/* @flow */
import React, {
  useContext, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useSubscription } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { Text, Button, ActivityIndicator } from 'react-native-paper';
import { useAppState } from 'react-native-hooks';

import { doneTodosSortedByTime, enhanceTodosWithOutbox } from '../modules/todo/selectors';
import l from '../utils/localization';
import ActionsContext from '../contexts/actions';
import { OverlayContextProvider } from '../contexts/OverlayContext';
import { useColors } from '../contexts/theme';
import { tokenSelector, isLoggedInSelector } from '../modules/auth/selectors';
import { RefreshingProvider } from '../contexts/RefreshingContext';
import DraggableList from '../components/DraggableList';
import ListFooter from '../components/DraggableList/ListFooter';
import EmptyComponent from '../components/EmptyComponent';
import Overlay from '../components/Overlay';
import TaskFragment from './TaskFragment';


const Query = gql`
query DoneTodos($token: ID!, $cursor: Int) {
  me(token: $token){
    todosWithStatus(first: 20, after: $cursor, status: done) {
      edges {
        node {
          ...TaskFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
}
${TaskFragment}
`;

const Subscription = gql`
subscription PatchedTask($token: ID!) {
  taskPatched(token: $token){
    ...TaskFragment
  }
}
${TaskFragment}
`;

const LoggedIn = ({ leftActions, rightActions, ...props }) => {
  const token = useSelector(tokenSelector),
        [patchedTodos, setPatchedTodos] = useState([]),
        doneTodosLocal = useSelector(doneTodosSortedByTime),
        {
          data, loading, error, fetchMore, refetch,
        } = useQuery(Query, {
          variables: { token },
        }),
        colors = useColors(),
        dataFromServer = data && data.me ? data.me.todosWithStatus.edges.map(({ node }) => node) : [],
        dataWithUnsyncedChanges = useSelector((state) => enhanceTodosWithOutbox(dataFromServer, state.offline.outbox, [...patchedTodos, ...doneTodosLocal])).filter((todo) => todo.status === 'done'),
        showAnimation = dataWithUnsyncedChanges.length === 0;

  const onSubscriptionData = ({ subscriptionData }: Object) => {
    const { taskPatched } = subscriptionData.data;
    if (taskPatched) {
      setPatchedTodos((prev) => [...prev, taskPatched]);
    }
  };

  useSubscription(Subscription, {
    variables: { token },
    onSubscriptionData,
    shouldResubscribe: true,
  });

  return (
    <>
      <RefreshingProvider onRefresh={() => refetch()}>
        <DraggableList
          data={dataWithUnsyncedChanges}
          leftActions={leftActions}
          rightActions={rightActions}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={() => !loading ? (
            <EmptyComponent>
              <Text style={{ color: colors.text }}>
                { error ? error.message : l.empty}
              </Text>
              <Button mode='contained' onPress={() => refetch()} color={colors.separator} style={{ marginTop: 10 }}>
                {l.refresh}
              </Button>
            </EmptyComponent>
          ) : <EmptyComponent><ActivityIndicator color={colors.text} /></EmptyComponent>}
          onEndReached={() => {
            if (!data || !data.me || !data.me.todosWithStatus.pageInfo.hasNextPage || loading) {
              return null;
            }

            return fetchMore({
              variables: {
                cursor: data.me.todosWithStatus.pageInfo.endCursor,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const newEdges = fetchMoreResult.me.todosWithStatus.edges,
                      { pageInfo } = fetchMoreResult.me.todosWithStatus;

                return newEdges.length
                  ? {
                  // Put the new comments at the end of the list and update `pageInfo`
                  // so we have the new `endCursor` and `hasNextPage` values
                    me: {
                      ...previousResult.me,
                      todosWithStatus: {
                        ...previousResult.me.todosWithStatus,
                        edges: [...previousResult.me.todosWithStatus.edges, ...newEdges],
                        pageInfo,
                      },
                    },
                  }
                  : previousResult;
              },
            });
          }}
          ListFooterComponent={ListFooter}
          {...props}
        />
        <Overlay showAnimation={showAnimation} />
      </RefreshingProvider>
    </>

  );
};

const NotLoggedIn = ({ leftActions, rightActions, ...props }) => {
  const data = useSelector(doneTodosSortedByTime),
        colors = useColors(),
        isEmpty = data.length === 0;

  return (
    <>
      <DraggableList
        data={data}
        leftActions={leftActions}
        rightActions={rightActions}
        ListFooterComponent={ListFooter}
        ListEmptyComponent={<EmptyComponent><Text style={{ color: colors.text }}>{ l.empty }</Text></EmptyComponent>}
        {...props}
      />
      <Overlay showAnimation={isEmpty} />
    </>

  );
};

const TodoListDone = (props: Object) => {
  const isLoggedIn = useSelector(isLoggedInSelector),
        appState = useAppState(),
        actions = useContext(ActionsContext),
        rightActions = useMemo(() => [actions.delete], [actions]),
        leftActions = useMemo(() => [actions.todo, actions.postpone], [actions]);

  return (
    <OverlayContextProvider hideAdd>
      { isLoggedIn && appState !== 'background'
        ? <LoggedIn leftActions={leftActions} rightActions={rightActions} {...props} />
        : <NotLoggedIn leftActions={leftActions} rightActions={rightActions} {...props} /> }
    </OverlayContextProvider>
  );
};

TodoListDone.whyDidYouRender = true;

export default TodoListDone;
